import { s } from "@kosmod/pancake";

import { renderBeat, Snapshot, useDisposable } from "@kosmod/graphics-3d";
import { Component, h, Ref, useRef } from "@lukekaalim/act";
import { a3 } from "@lukekaalim/act-three";
import * as three from 'three';


import { useJeffect, Vector2 } from "@kosmod/kit";
import { TheatreState } from "../../hooks/useTheatreState";
import { getChunkDataIndex, iterateChunkDataCoords } from "../../lib/chunks";
import { TerrainCanvasID, TheatrePosition, tools } from "../../defs";
import { defineToolHandler } from "./handler";
import { html } from "@lukekaalim/act-web";
import { SlotStateIcon } from "./ToolSlotRenderer";
import { assetsURLs } from "../../assets";

type TerrainBoxPreviewProps = {
  cursor: Ref<null | TheatrePosition>,
  material?: three.Material,
  start?: TheatrePosition | null,
}
const TerrainBoxPreview: Component<TerrainBoxPreviewProps> = ({
  cursor,
  start,
  material,
}) => {
  const onRender = renderBeat.useBeat();
  const ref = useRef<null | three.Object3D>(null);

  useJeffect(j => {
    const mesh = ref.get()!;
    
    j.add(onRender(() => {
      const cursorPosition = cursor.get();
      mesh.visible = !!cursorPosition;
      if (cursorPosition) {
        if (!start) {
          const start = getSnappedPosition(cursorPosition);
          TheatrePosition.copyToVector3(start, mesh.position)
            .add({ x: .5, y: 0, z: .5 });
          mesh.scale.set(1, 1, 1);

        } else {
          const box = getSnappedBox(cursorPosition, start);

          const size = box.getSize(new three.Vector2());
          mesh.position.copy({ x: box.min.x, y: 0, z: box.min.y })
            .add({ x: size.x / 2, y: 0, z: size.y / 2 })
          mesh.scale.copy({ x: size.x, y: 1, z: size.y })
        }
      }
    }))
  }, [cursor, start])

  const geometry = useDisposable(() => new three.BoxGeometry(1, 0.02, 1), [])

  return h(a3.mesh, { ref, geometry, material });
}

const getSnappedPosition = (cursor: TheatrePosition) => {
  return TheatrePosition.snapToSquareGrid(cursor);
};
const getSnappedBox = (cursor: TheatrePosition, start: TheatrePosition) => {
  const gridPosition = TheatrePosition.snapToSquareGrid(cursor);
  const end = gridPosition;
  const box = new three.Box2().setFromPoints([
    new three.Vector2(start.vector.x, start.vector.y),
    new three.Vector2(end.vector.x, end.vector.y),
  ]);
  box.max.addScalar(1);
  return box;
}

type TerrainLinePreviewProps = {
  cursor: Ref<null | TheatrePosition>,
  material?: three.Material,
  start?: TheatrePosition | null,
}
const TerrainLinePreview: Component<TerrainLinePreviewProps> = ({
  cursor,
  start,
  material,
}) => {
  const onRender = renderBeat.useBeat();
  const ref = useRef<null | three.Object3D>(null);

  useJeffect(j => {
    const mesh = ref.get()!;
    
    j.add(onRender(() => {
      const cursorPosition = cursor.get();
      if (cursorPosition) {
        mesh.visible = true;
        if (!start) {
          const start = TheatrePosition.snapToSquareGridVertex(cursorPosition);
          TheatrePosition.copyToVector3(start, mesh.position)
            .add({ x: 0, y: .5, z: 0 })
          mesh.scale.set(1, 1, 1);
        } else {
          const end = TheatrePosition.snapToSquareGridVertex(cursorPosition);
          const distance = new three.Vector2()
            .copy(start.vector)
            .sub(end.vector);
            
          if (Math.abs(distance.x) > Math.abs(distance.y)) {
            mesh.scale.copy({ x: -distance.x * 5, y: 1, z: 1 })
            TheatrePosition.copyToVector3(start, mesh.position)
              .add({ x: -distance.x / 2, y: .5, z: 0 })
            // horizontal
          } else {
            // vertical
            mesh.scale.copy({ x: 1, y: 1, z: -distance.y * 5 })
              TheatrePosition.copyToVector3(start, mesh.position)
              .add({ x: 0, y: .5, z: -distance.y / 2 })
          }

        }
      } else {
        mesh.visible = false;
      }
    }))
  }, [cursor, start])

  const geometry = useDisposable(() => new three.BoxGeometry(.2, 1, .2), [])

  return h(a3.mesh, { geometry, material, ref })
}

defineToolHandler(tools.terrain.paint.brush, {
  def: tools.terrain.paint.brush,

  onSelect(state, tools, selection, logger, controller, data) {
    if (tools.activeTool === state.id) {
      tools.setToolState(state.id, { ...state, part: state.part === 'floor' ? 'wall' : 'floor' })
    }
  },

  onInput(event, state, theatre, _, rawEvent) {
    if (event.type === 'mouse') {
      if (event.button === 'secondary') {
        theatre.tools.setToolState(state.id, { ...state, start: null })
        rawEvent.preventDefault();
        return;
      }
      const cursor = theatre.cursorPosition.get();
      const canvas = cursor && theatre.data.canvases.find(c => c.surface === cursor.surfaceId);

      if (cursor && canvas) {
        if (!state.start) {
          switch (state.part) {
            case 'floor':
              theatre.tools.setToolState(state.id, { ...state, start: TheatrePosition.snapToSquareGrid(cursor) })
              break;
            case 'wall':
              theatre.tools.setToolState(state.id, { ...state, start: TheatrePosition.snapToSquareGridVertex(cursor) })
              break;
          }
        } else {
          // paint
          switch (state.part) {
            case 'floor': {
              const end = TheatrePosition.snapToSquareGrid(cursor);

              const box = new three.Box2().setFromPoints([
                new three.Vector2().copy(state.start.vector),
                new three.Vector2().copy(end.vector),
              ]);
              box.max.addScalar(1);
              
              theatre.controller.paint({
                canvas: canvas.id,
                brush: state.brush,
                layer: 'floor',
                shape: { type: 'box', box: {
                  start: box.min,
                  end: box.max,
                } }
              });
              break;
            }
            case 'wall': {
              const cursorVertex = TheatrePosition.snapToSquareGridVertex(cursor);
              const distance = new three.Vector2()
                .copy(cursorVertex.vector)
                .sub(state.start.vector);

              const horizontal = Math.abs(distance.x) > Math.abs(distance.y);
              const axis = horizontal
                ? new three.Vector2(1, 0)
                : new three.Vector2(0, 1)

              distance.multiply(axis);

              const startVector = new three.Vector2().copy(state.start.vector);
              const endVector = startVector.clone().add(distance);

              const start = startVector.clone().min(endVector)
              const end = startVector.clone().max(endVector).add({ x: axis.y, y: axis.x })

              theatre.controller.paint({ shape: {
                type: 'box',
                box: { start, end, },
              }, brush: state.brush, canvas: canvas.id, layer: horizontal ? 'top-wall' : 'left-wall' })
              break;
            }
          }

          theatre.tools.setToolState(state.id, { ...state, start: null })
        }
      }
    }
    if (event.type === 'keyboard' && event.code === 'KeyZ') {
      theatre.tools.setToolState(state.id, { ...state, part: state.part === 'floor' ? 'wall' : 'floor' })
    }
  },
  PreviewComponent({ cursorPosition, material, toolState }) {
    if (toolState.part === 'wall') {
      return h(TerrainLinePreview, { cursor: cursorPosition, material, start: toolState.start })
    }
    if (toolState.part === 'floor') {
      return h(TerrainBoxPreview, { cursor: cursorPosition, material, start: toolState.start })
    }
    return null;
  },
  ToolbeltComponent({ toolState, theatre }) {
    const brush = toolState.brush && theatre.data.brushes.get(toolState.brush);
    const dioramaModel = brush && theatre.sceneRes.brushParts.get(brush.id)?.diorama || null;

    return [
      h(html.div, { style: { position: 'absolute', color: 'black', bottom: '0px' } }, brush ? brush.name : 'Erase'),

      h(Snapshot, { object: dioramaModel, graphics: theatre.graphics }),
      h(SlotStateIcon, { },
        h(html.img, {
          src: toolState.part === 'floor'
            ? assetsURLs.icons.floor
            : assetsURLs.icons.wall
        })),
    ];
  }
});